import type { PlatformSubpageConfig } from '../types';
import { defaultCtas, localImage } from './shared';

const img = (src: string, alt: string) => ({
  src: localImage(src),
  alt,
  width: 1200,
  height: 800,
});

export const dashboardConfig: PlatformSubpageConfig = {
  slug: 'dashboard',
  path: '/platform/dashboard',
  breadcrumbLabel: 'Dashboard',
  hero: {
    title: 'Operate your entire fleet from one partner dashboard',
    subtitle:
      'Manage customers, devices, firmware, analytics, and support from one centralized admin experience.',
    ...defaultCtas,
  },
  heroImage: img('/images/home/partner-dashboard.png', 'Partner admin dashboard'),
  eduIntro: {
    title: 'What is a partner operations dashboard?',
    description:
      'A partner dashboard centralizes fleet monitoring, tenant management, analytics, and remote actions—giving operators real-time visibility without juggling multiple tools.',
  },
  advantages: {
    eyebrow: 'COMPETITIVE EDGE',
    title: 'Operate your entire fleet from one place',
    items: [
      { icon: 'bar-chart', title: 'Unified Operations', description: 'Monitor devices, users, and firmware from a single workspace.' },
      { icon: 'activity', title: 'Real-Time Visibility', description: 'Live status, alerts, and usage trends across tenants and regions.' },
      { icon: 'layers', title: 'Multi-Tenant Control', description: 'Isolate partner data with roles, permissions, and scoped admin tools.' },
    ],
  },
  featureShowcase: {
    eyebrow: 'DASHBOARD MODULES',
    title: 'Dashboard modules built for daily operations',
    subtitle: 'Drill into monitoring, analytics, and tenant tools.',
    items: [
      {
        title: 'Device monitoring',
        description: 'Live online status, last-seen, firmware version, and alert thresholds per device.',
        image: img('/images/home/partner-dashboard.png', 'Device monitoring'),
        cta: { label: 'Explore monitoring', to: '/contact-us' },
      },
      {
        title: 'Usage analytics',
        description: 'Activation trends, energy usage, and feature adoption across tenants and regions.',
        image: img('/images/home/feature-cloud.jpg', 'Analytics'),
        cta: { label: 'View analytics', to: '/contact-us' },
      },
      {
        title: 'Tenant management',
        description: 'Isolate partner data, roles, and billing with multi-tenant workspaces.',
        image: img('/images/home/white-label-app-image.png', 'Tenant management'),
        cta: { label: 'Manage tenants', to: '/contact-us' },
      },
    ],
  },
  capabilities: {
    eyebrow: 'CAPABILITIES',
    title: 'Admin and analytics tools for growing partners',
    items: [
      { icon: 'activity', title: 'Device Monitoring', description: 'Online status, alerts, and fleet health at a glance.' },
      { icon: 'chart', title: 'Usage Analytics', description: 'Activation, engagement, and feature adoption metrics.' },
      { icon: 'key', title: 'Role Management', description: 'Granular permissions for ops, support, and engineering.' },
      { icon: 'layers', title: 'Tenant Management', description: 'Workspaces for brands, regions, and business units.' },
      { icon: 'server', title: 'Device Logs', description: 'Searchable logs and diagnostics for faster support.' },
      { icon: 'zap', title: 'Remote Actions', description: 'Reboot, reset, and push config without field visits.' },
    ],
  },
  ctaBanner: {
    title: 'Take control of your fleet with a unified partner dashboard',
    ctaLabel: 'Get Started',
    ctaTo: '/contact-us',
    imageSrc: '/images/cta/solution-cta-robot.webp',
  },
  faqs: {
    title: 'Frequently asked questions',
    items: [
      { question: 'Can I manage multiple tenants?', answer: 'Yes—multi-tenant workspaces with role-based access and data isolation.' },
      { question: 'What analytics are included?', answer: 'Activation, usage, firmware adoption, and custom export options.' },
      { question: 'Are remote device actions supported?', answer: 'Reboot, reset, log pull, and config push from the dashboard.' },
    ],
  },
  sections: {
    eyebrow: 'ARCHITECTURE',
    title: 'Dashboard modules built for daily operations',
    subtitle: 'Drill into monitoring, analytics, and tenant tools.',
  },
};

export const dashboardModules = [
  {
    id: 'monitoring',
    title: 'Device Monitoring',
    description: 'Live online status, last-seen, firmware version, and alert thresholds per device.',
  },
  {
    id: 'analytics',
    title: 'Usage Analytics',
    description: 'Activation trends, energy usage, and feature adoption across tenants and regions.',
  },
  {
    id: 'tenants',
    title: 'Tenant Management',
    description: 'Isolate partner data, roles, and billing with multi-tenant workspaces.',
  },
] as const;
